import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { ChevronDown, ChevronUp, Plus, Trash2, Settings2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import ChemicalAutocomplete from './ChemicalAutocomplete';
import { GHS_SYMBOL_OPTIONS } from './GHSIcons';
import { smartMatchChemical, applyTemplateToChemical } from '../data/chemicalDatabase';

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-md mb-2 bg-white overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <span className="font-semibold text-sm text-slate-800">{title}</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="p-3 space-y-2">{children}</div>}
    </div>
  );
}

function TextInput({ label, value, onChange }) {
  return (
    <div>
      <Label className="text-xs text-slate-700 mb-1 block">{label}</Label>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm h-9"
      />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <Label className="text-xs text-slate-700 mb-1 block">{label}</Label>
      <Textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="text-sm"
      />
    </div>
  );
}

function ListEditor({ label, items, onChange, addLabel }) {
  return (
    <div>
      <Label className="text-xs text-slate-700 mb-1 block">{label}</Label>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex gap-1">
            <Textarea
              value={item}
              onChange={(e) => {
                const next = [...items];
                next[i] = e.target.value;
                onChange(next);
              }}
              rows={2}
              className="text-sm flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              className="h-9 w-9 flex-shrink-0 text-red-500 hover:text-red-600"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...items, ''])}
          className="text-xs h-8 w-full"
        >
          <Plus size={12} className="ml-1" /> {addLabel}
        </Button>
      </div>
    </div>
  );
}

export default function SDSEditorPanel({ data, setData }) {
  const { t, i18n } = useTranslation();
  const addLabel = t('sds.panel.addItem');

  const update = (path, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const updChem = (id, field, val) =>
    setData((p) => ({
      ...p,
      chemicals: p.chemicals.map((c) => {
        if (c.id !== id) return c;
        let updated = { ...c, [field]: val };
        if (field === 'name') {
          updated.risk = { ...updated.risk, material: val };
          const tpl = smartMatchChemical(val);
          if (tpl) {
            updated = applyTemplateToChemical(updated, tpl);
            updated.name = val;
            updated.risk.material = val;
          }
        }
        return updated;
      }),
    }));

  const applyChemicalTemplate = (id, chem) => {
    setData((p) => ({
      ...p,
      chemicals: p.chemicals.map((c) =>
        c.id === id
          ? {
              ...c,
              name: chem.name,
              chemicalName: chem.chemicalName,
              unNumber: chem.unNumber,
              ghsClassification: chem.ghsClassification,
              ghsSymbols: [...chem.ghsSymbols],
              usage: chem.usage,
              image: chem.image,
              nfpa: { ...chem.nfpa },
              risk: { ...chem.risk, material: chem.name },
            }
          : c
      ),
    }));
  };

  const updNFPA = (id, field, val) =>
    setData((p) => ({
      ...p,
      chemicals: p.chemicals.map((c) =>
        c.id === id ? { ...c, nfpa: { ...c.nfpa, [field]: val } } : c
      ),
    }));

  const toggleSymbol = (id, symbol) =>
    setData((p) => ({
      ...p,
      chemicals: p.chemicals.map((c) => {
        if (c.id !== id) return c;
        const has = c.ghsSymbols.includes(symbol);
        return {
          ...c,
          ghsSymbols: has ? c.ghsSymbols.filter((s) => s !== symbol) : [...c.ghsSymbols, symbol],
        };
      }),
    }));

  const nfpaLabel = (f) => {
    if (f === 'health') return t('sds.panel.nfpaHealth');
    if (f === 'fire') return t('sds.panel.nfpaFire');
    if (f === 'reactivity') return t('sds.panel.nfpaReactivity');
    return t('sds.panel.nfpaSpecial');
  };

  return (
    <div className="sidebar-scroll bg-white shadow-xl rounded-lg p-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 130px)' }}>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b">
        <Settings2 className="text-[#1a4d80]" size={20} />
        <h2 className="font-bold text-base text-slate-800">{t('sds.panel.title')}</h2>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        {t('sds.panel.tip')}
      </p>

      <Section title={t('sds.panel.header')} defaultOpen>
        <TextInput
          label={t('sds.panel.headerMain')}
          value={data.header.title}
          onChange={(v) => update('header.title', v)}
        />
        <TextInput
          label={t('sds.panel.headerSub')}
          value={data.header.subtitle}
          onChange={(v) => update('header.subtitle', v)}
        />
        <TextInput
          label={t('sds.panel.headerAuthAr')}
          value={data.header.authorityName}
          onChange={(v) => update('header.authorityName', v)}
        />
        <TextInput
          label={t('sds.panel.headerAuthEn')}
          value={data.header.authorityNameEn}
          onChange={(v) => update('header.authorityNameEn', v)}
        />
      </Section>

      <Section title={t('sds.panel.facilityInfo')} defaultOpen>

        {/* Facility / factory logo */}
        <div className="mb-4 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-3 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <div className="text-sm font-bold text-slate-800">
                شعار المصنع
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                يظهر الشعار في رأس تقرير SDS وفي PDF والصورة
              </div>
            </div>

            {data.facility?.logoDataUrl && (
              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1">
                تمت الإضافة
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="w-[110px] h-[82px] bg-white rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
              {data.facility?.logoDataUrl ? (
                <img
                  src={data.facility.logoDataUrl}
                  alt="شعار المصنع"
                  className="max-w-full max-h-full object-contain p-2"
                />
              ) : (
                <div className="text-center text-slate-400 px-2">
                  <div className="text-2xl mb-1">🏭</div>
                  <div className="text-[9px]">لا يوجد شعار</div>
                </div>
              )}
            </div>

            <div className="flex-1 space-y-2">
              <label className="flex items-center justify-center cursor-pointer rounded-lg bg-[#1a4d80] hover:bg-[#143d68] text-white text-xs font-bold px-3 py-2 transition-colors">
                {data.facility?.logoDataUrl ? 'تغيير شعار المصنع' : 'رفع شعار المصنع'}

                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    const allowed = [
                      'image/png',
                      'image/jpeg',
                      'image/jpg',
                      'image/webp'
                    ];

                    if (!allowed.includes(file.type)) {
                      window.alert('صيغة الشعار غير مدعومة. استخدم PNG أو JPG أو WEBP.');
                      e.target.value = '';
                      return;
                    }

                    if (file.size > 2 * 1024 * 1024) {
                      window.alert('حجم الشعار يجب ألا يتجاوز 2MB.');
                      e.target.value = '';
                      return;
                    }

                    const img = new Image();
                    const reader = new FileReader();

                    reader.onload = () => {
                      img.onload = () => {
                        /*
                         * Normalize large logos before storing them in SDS data.
                         * Keeps PDFs sharp without making the report excessively large.
                         */
                        const MAX_W = 800;
                        const MAX_H = 500;

                        let w = img.width;
                        let h = img.height;

                        const scale = Math.min(
                          1,
                          MAX_W / Math.max(w, 1),
                          MAX_H / Math.max(h, 1)
                        );

                        w = Math.max(1, Math.round(w * scale));
                        h = Math.max(1, Math.round(h * scale));

                        const canvas = document.createElement('canvas');
                        canvas.width = w;
                        canvas.height = h;

                        const ctx = canvas.getContext('2d');
                        ctx.clearRect(0, 0, w, h);
                        ctx.drawImage(img, 0, 0, w, h);

                        const mime =
                          file.type === 'image/png'
                            ? 'image/png'
                            : 'image/jpeg';

                        const logoDataUrl = canvas.toDataURL(
                          mime,
                          mime === 'image/png' ? 1 : 0.92
                        );

                        update('facility.logoDataUrl', logoDataUrl);
                        update('facility.logoFileName', file.name);
                      };

                      img.src = reader.result;
                    };

                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }}
                />
              </label>

              {data.facility?.logoDataUrl && (
                <button
                  type="button"
                  onClick={() => {
                    update('facility.logoDataUrl', '');
                    update('facility.logoFileName', '');
                  }}
                  className="w-full rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold px-3 py-2 transition-colors"
                >
                  حذف الشعار
                </button>
              )}

              <div className="text-[9px] text-slate-400 leading-relaxed">
                PNG أو JPG أو WEBP — الحد الأقصى 2MB
              </div>
            </div>
          </div>
        </div>

        <TextInput
          label={t('sds.facility.name')}
          value={data.facility?.name || ''}
          onChange={(v) => update('facility.name', v)}
        />
        <TextInput
          label={t('sds.facility.activity')}
          value={data.facility?.activity || ''}
          onChange={(v) => update('facility.activity', v)}
        />
        <TextInput
          label={t('sds.facility.location')}
          value={data.facility?.location || ''}
          onChange={(v) => update('facility.location', v)}
        />

        {/* Facility land area + four-side setbacks + 30% storage */}
        {(() => {
          const length = Math.max(
            0,
            Number(data.facility?.plotLengthM || 0)
          );

          const width = Math.max(
            0,
            Number(data.facility?.plotWidthM || 0)
          );

          const north = Math.max(
            0,
            Number(data.facility?.setbackNorthM || 0)
          );

          const south = Math.max(
            0,
            Number(data.facility?.setbackSouthM || 0)
          );

          const east = Math.max(
            0,
            Number(data.facility?.setbackEastM || 0)
          );

          const west = Math.max(
            0,
            Number(data.facility?.setbackWestM || 0)
          );

          const grossArea =
            length > 0 && width > 0
              ? length * width
              : 0;

          const netLength = length - north - south;
          const netWidth = width - east - west;

          const valid =
            length > 0 &&
            width > 0 &&
            netLength > 0 &&
            netWidth > 0;

          const netArea =
            valid ? netLength * netWidth : 0;

          const storageLimit =
            valid ? netArea * 0.30 : 0;

          const rawMaterialStorageArea =
            Math.max(
              0,
              Number(
                data.facility?.rawMaterialStorageAreaM2 || 0
              )
            );

          const finishedProductStorageArea =
            Math.max(
              0,
              Number(
                data.facility?.finishedProductStorageAreaM2 || 0
              )
            );

          const totalFacilityStorageArea =
            rawMaterialStorageArea +
            finishedProductStorageArea;

          const remainingFacilityStorageArea =
            storageLimit -
            totalFacilityStorageArea;

          const facilityStorageUsagePercent =
            storageLimit > 0
              ? (
                  totalFacilityStorageArea /
                  storageLimit
                ) * 100
              : 0;

          const fmt = (n) =>
            Number(n || 0).toLocaleString('ar-SA', {
              maximumFractionDigits: 2
            });

          const normalizeAreaNumber = (raw) => {
            let v = String(raw ?? '');

            // Arabic-Indic digits
            v = v.replace(/[٠-٩]/g, (d) =>
              String('٠١٢٣٤٥٦٧٨٩'.indexOf(d))
            );

            // Persian/Urdu digits
            v = v.replace(/[۰-۹]/g, (d) =>
              String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
            );

            // Arabic / English decimal separators
            v = v
              .replace(/٫/g, '.')
              .replace(/,/g, '.')
              .replace(/٬/g, '');

            // Numbers + one decimal point only
            v = v.replace(/[^0-9.]/g, '');

            const firstDot = v.indexOf('.');
            if (firstDot !== -1) {
              v =
                v.slice(0, firstDot + 1) +
                v.slice(firstDot + 1).replace(/\./g, '');
            }

            return v;
          };

          const areaInput = (
            label,
            path,
            value,
            placeholder
          ) => (
            <div>
              <Label className="text-[11px] font-bold text-slate-700 mb-1 block">
                {label}
              </Label>

              <Input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={value ?? ''}
                placeholder={placeholder}
                onChange={(e) => {
                  const clean = normalizeAreaNumber(
                    e.target.value
                  );
                  update(path, clean);
                }}
                className="h-10 text-sm"
                dir="ltr"
              />
            </div>
          );

          return (
            <div
              dir="rtl"
              className="mt-4 rounded-xl border-[1.5px] border-[#b8c8d9] bg-white p-3 shadow-sm"
            >

              <div className="mb-3">
                <div className="text-sm font-bold text-[#1a4d80]">
                  مساحة الأرض والارتدادات
                </div>

                <div className="mt-1 text-[10px] leading-relaxed text-slate-500">
                  يتم حساب مساحة الأرض من الطول × العرض، ثم خصم الارتدادات من الجهات الأربع، ثم احتساب الحد الأعلى للتخزين بنسبة 30%.
                </div>
              </div>

              {/* Land dimensions */}
              <div className="grid grid-cols-2 gap-2">

                {areaInput(
                  'طول الأرض (م)',
                  'facility.plotLengthM',
                  data.facility?.plotLengthM,
                  '100'
                )}

                {areaInput(
                  'عرض الأرض (م)',
                  'facility.plotWidthM',
                  data.facility?.plotWidthM,
                  '50'
                )}

              </div>

              {/* Gross area */}
              <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-center">

                <div className="text-[10px] font-semibold text-blue-700">
                  مساحة الأرض الإجمالية
                </div>

                <div className="mt-1 text-xl font-extrabold text-[#1a4d80]">
                  {fmt(grossArea)} م²
                </div>

                {grossArea > 0 && (
                  <div className="mt-1 text-[9px] text-slate-500">
                    {fmt(length)} × {fmt(width)}
                  </div>
                )}

              </div>

              {/* Setbacks */}
              <div className="mt-4">

                <div className="mb-2 text-xs font-bold text-slate-700">
                  الارتدادات من حدود الأرض
                </div>

                <div className="grid grid-cols-2 gap-2">

                  {areaInput(
                    'ارتداد الشمال (م)',
                    'facility.setbackNorthM',
                    data.facility?.setbackNorthM,
                    '4'
                  )}

                  {areaInput(
                    'ارتداد الجنوب (م)',
                    'facility.setbackSouthM',
                    data.facility?.setbackSouthM,
                    '4'
                  )}

                  {areaInput(
                    'ارتداد الشرق (م)',
                    'facility.setbackEastM',
                    data.facility?.setbackEastM,
                    '4'
                  )}

                  {areaInput(
                    'ارتداد الغرب (م)',
                    'facility.setbackWestM',
                    data.facility?.setbackWestM,
                    '4'
                  )}

                </div>

              </div>

              {grossArea > 0 && !valid && (
                <div className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[11px] font-bold text-red-700">
                  تحقق من أبعاد الأرض والارتدادات؛ يجب أن يبقى الطول والعرض الصافي أكبر من صفر.
                </div>
              )}

              {valid && (
                <div className="mt-4 space-y-2">

                  <div className="grid grid-cols-2 gap-2">

                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center">
                      <div className="text-[10px] text-slate-500">
                        الطول الصافي
                      </div>
                      <div className="mt-1 font-bold text-[#1a4d80]">
                        {fmt(netLength)} م
                      </div>
                      <div className="mt-1 text-[9px] text-slate-400">
                        الطول − الشمال − الجنوب
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-center">
                      <div className="text-[10px] text-slate-500">
                        العرض الصافي
                      </div>
                      <div className="mt-1 font-bold text-[#1a4d80]">
                        {fmt(netWidth)} م
                      </div>
                      <div className="mt-1 text-[9px] text-slate-400">
                        العرض − الشرق − الغرب
                      </div>
                    </div>

                  </div>

                  <div className="rounded-lg border border-[#b8c8d9] bg-white p-3 text-center">

                    <div className="text-[10px] font-semibold text-slate-500">
                      المساحة الصافية بعد الارتدادات
                    </div>

                    <div className="mt-1 text-xl font-extrabold text-[#1a4d80]">
                      {fmt(netArea)} م²
                    </div>

                    <div className="mt-1 text-[9px] text-slate-400">
                      {fmt(netLength)} × {fmt(netWidth)}
                    </div>

                  </div>

                  <div className="col-span-2 mt-2 rounded-xl border border-[#b8c8d9] bg-slate-50 p-3">

                    <div className="mb-3">
                      <div className="text-xs font-bold text-[#1a4d80]">
                        مساحات التخزين داخل المنشأة
                      </div>

                      <div className="mt-1 text-[9px] text-slate-500">
                        المواد الخام والمنتج النهائي يشتركان معًا في حد التخزين 30%.
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">

                      {areaInput(
                        'مساحة تخزين المواد الخام (م²)',
                        'facility.rawMaterialStorageAreaM2',
                        data.facility?.rawMaterialStorageAreaM2,
                        'مثال: 700'
                      )}

                      {areaInput(
                        'مساحة تخزين المنتج النهائي (م²)',
                        'facility.finishedProductStorageAreaM2',
                        data.facility?.finishedProductStorageAreaM2,
                        'مثال: 350'
                      )}

                    </div>

                  </div>

                  <div className="col-span-2 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 text-center">

                    <div className="text-[11px] font-bold text-emerald-800">
                      الحد الأعلى المسموح لمساحة التخزين
                    </div>

                    <div className="mt-1 text-2xl font-extrabold text-emerald-700">
                      {fmt(storageLimit)} م²
                    </div>

                    <div className="mt-1 text-[10px] font-semibold text-emerald-700">
                      30% من المساحة الصافية
                    </div>

                  </div>

                  <div className="col-span-2 grid grid-cols-2 gap-2">

                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-center">
                      <div className="text-[9px] text-amber-700">
                        المواد الخام
                      </div>

                      <div className="font-extrabold text-amber-800">
                        {fmt(rawMaterialStorageArea)} م²
                      </div>
                    </div>

                    <div className="rounded-lg border border-violet-200 bg-violet-50 p-2 text-center">
                      <div className="text-[9px] text-violet-700">
                        المنتج النهائي
                      </div>

                      <div className="font-extrabold text-violet-800">
                        {fmt(finishedProductStorageArea)} م²
                      </div>
                    </div>

                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-center">
                      <div className="text-[9px] text-blue-700">
                        إجمالي مساحة التخزين
                      </div>

                      <div className="font-extrabold text-[#1a4d80]">
                        {fmt(totalFacilityStorageArea)} م²
                      </div>
                    </div>

                    <div
                      className={`rounded-lg border p-2 text-center ${
                        remainingFacilityStorageArea >= 0
                          ? 'border-emerald-200 bg-emerald-50'
                          : 'border-red-300 bg-red-50'
                      }`}
                    >
                      <div className="text-[9px] text-slate-600">
                        {remainingFacilityStorageArea >= 0
                          ? 'المتبقي'
                          : 'التجاوز'}
                      </div>

                      <div
                        className={`font-extrabold ${
                          remainingFacilityStorageArea >= 0
                            ? 'text-emerald-700'
                            : 'text-red-700'
                        }`}
                      >
                        {fmt(
                          Math.abs(
                            remainingFacilityStorageArea
                          )
                        )} م²
                      </div>
                    </div>

                  </div>

                  <div
                    className={`col-span-2 rounded-xl border-2 p-3 text-center ${
                      totalFacilityStorageArea <= storageLimit
                        ? 'border-emerald-300 bg-emerald-50'
                        : 'border-red-400 bg-red-50'
                    }`}
                  >

                    <div className="text-[10px] text-slate-600">
                      نسبة استخدام الحد المسموح
                    </div>

                    <div
                      className={`mt-1 text-xl font-extrabold ${
                        totalFacilityStorageArea <= storageLimit
                          ? 'text-emerald-700'
                          : 'text-red-700'
                      }`}
                    >
                      {fmt(facilityStorageUsagePercent)}%
                    </div>

                    <div
                      className={`mt-1 text-[11px] font-bold ${
                        totalFacilityStorageArea <= storageLimit
                          ? 'text-emerald-700'
                          : 'text-red-700'
                      }`}
                    >
                      {totalFacilityStorageArea <= storageLimit
                        ? '✓ مساحة التخزين ضمن الحد المسموح'
                        : '⚠ تجاوز الحد الأعلى المسموح للتخزين'}
                    </div>

                  </div>

                </div>
              )}

            </div>
          );
        })()}

      </Section>

      <Section title={`${t('sds.panel.chemicals')} (${data.chemicals.length})`} defaultOpen>
        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 mb-2 flex items-start gap-2">
          <Sparkles size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] text-emerald-900 leading-snug">
            <Trans
              i18nKey="sds.panel.autoTip"
              components={{ b: <strong /> }}
            />
          </div>
        </div>
        {(() => {

          const normalizeStorageNumber = (raw) => {
            let v = String(raw ?? '');

            v = v.replace(/[٠-٩]/g, (d) =>
              String('٠١٢٣٤٥٦٧٨٩'.indexOf(d))
            );

            v = v.replace(/[۰-۹]/g, (d) =>
              String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
            );

            v = v
              .replace(/٫/g, '.')
              .replace(/,/g, '.')
              .replace(/٬/g, '');

            v = v.replace(/[^0-9.]/g, '');

            const firstDot = v.indexOf('.');
            if (firstDot !== -1) {
              v =
                v.slice(0, firstDot + 1) +
                v.slice(firstDot + 1).replace(/\./g, '');
            }

            return v;
          };

          const packagePresets = {
            drum200: {
              label: 'برميل 200 لتر',
              capacityL: 200,
              lengthM: 0.60,
              widthM: 0.60
            },
            jerry20: {
              label: 'جركن 20 لتر',
              capacityL: 20,
              lengthM: 0.292,
              widthM: 0.246
            },
            jerry25: {
              label: 'جركن 25 لتر',
              capacityL: 25,
              lengthM: 0.292,
              widthM: 0.246
            },
            ibc1000: {
              label: 'IBC سعة 1000 لتر',
              capacityL: 1000,
              lengthM: 1.20,
              widthM: 1.00
            },
            bottle5: {
              label: 'عبوة 5 لتر',
              capacityL: 5,
              lengthM: 0.196,
              widthM: 0.143
            },
            custom: {
              label: 'مقاس مخصص',
              capacityL: 0,
              lengthM: 0,
              widthM: 0
            }
          };

          const palletPresets = {
            iso: {
              label: 'طبلية 120 × 100 سم',
              lengthM: 1.20,
              widthM: 1.00
            },
            euro: {
              label: 'طبلية Euro 120 × 80 سم',
              lengthM: 1.20,
              widthM: 0.80
            },
            custom: {
              label: 'طبلية بمقاس مخصص',
              lengthM: 0,
              widthM: 0
            }
          };

          const getChemicalStorage = (chem) => {
            const packageType =
              chem.storagePackageType || 'drum200';

            const preset =
              packagePresets[packageType] ||
              packagePresets.drum200;

            const capacityL =
              packageType === 'custom'
                ? Math.max(
                    0,
                    Number(chem.storagePackageCapacityL || 0)
                  )
                : preset.capacityL;

            const packageLength =
              packageType === 'custom'
                ? Math.max(
                    0,
                    Number(chem.storagePackageLengthM || 0)
                  )
                : preset.lengthM;

            const packageWidth =
              packageType === 'custom'
                ? Math.max(
                    0,
                    Number(chem.storagePackageWidthM || 0)
                  )
                : preset.widthM;

            const quantity =
              Math.max(
                0,
                Number(chem.storageQuantity || 0)
              );

            const unit =
              chem.storageQuantityUnit || 'liter';

            const density =
              Math.max(
                0,
                Number(chem.storageDensityKgL || 0)
              );

            let totalLiters = 0;
            let packageCount = 0;
            let densityRequired = false;

            if (unit === 'liter') {
              totalLiters = quantity;

              if (capacityL > 0) {
                packageCount =
                  Math.ceil(totalLiters / capacityL);
              }
            }

            if (unit === 'kg') {
              if (density > 0) {
                totalLiters = quantity / density;

                if (capacityL > 0) {
                  packageCount =
                    Math.ceil(totalLiters / capacityL);
                }
              } else if (quantity > 0) {
                densityRequired = true;
              }
            }

            if (unit === 'ton') {
              if (density > 0) {
                totalLiters =
                  (quantity * 1000) / density;

                if (capacityL > 0) {
                  packageCount =
                    Math.ceil(totalLiters / capacityL);
                }
              } else if (quantity > 0) {
                densityRequired = true;
              }
            }

            if (unit === 'count') {
              packageCount =
                Math.ceil(quantity);

              totalLiters =
                packageCount * capacityL;
            }

            const layers = Math.min(
              3,
              Math.max(
                1,
                Number(chem.storageLayers || 1)
              )
            );

            const method =
              chem.storageMethod || 'direct';

            let baseArea = 0;
            let palletCount = 0;
            let groundPositions = 0;

            if (
              method === 'direct' &&
              packageCount > 0 &&
              packageLength > 0 &&
              packageWidth > 0
            ) {
              groundPositions =
                Math.ceil(packageCount / layers);

              baseArea =
                groundPositions *
                packageLength *
                packageWidth;
            }

            if (method === 'pallet') {
              const palletType =
                chem.storagePalletType || 'iso';

              const palletPreset =
                palletPresets[palletType] ||
                palletPresets.iso;

              const palletLength =
                palletType === 'custom'
                  ? Math.max(
                      0,
                      Number(
                        chem.storagePalletLengthM || 0
                      )
                    )
                  : palletPreset.lengthM;

              const palletWidth =
                palletType === 'custom'
                  ? Math.max(
                      0,
                      Number(
                        chem.storagePalletWidthM || 0
                      )
                    )
                  : palletPreset.widthM;

              const packagesPerPallet =
                Math.max(
                  0,
                  Math.floor(
                    Number(
                      chem.storagePackagesPerPallet || 0
                    )
                  )
                );

              if (
                packageCount > 0 &&
                packagesPerPallet > 0
              ) {
                palletCount =
                  Math.ceil(
                    packageCount /
                    packagesPerPallet
                  );

                groundPositions =
                  Math.ceil(
                    palletCount / layers
                  );

                baseArea =
                  groundPositions *
                  palletLength *
                  palletWidth;
              }
            }

            const extraArea =
              Math.max(
                0,
                Number(
                  chem.storageExtraAreaM2 || 0
                )
              );

            const totalArea =
              baseArea + extraArea;

            return {
              packageType,
              capacityL,
              packageLength,
              packageWidth,
              quantity,
              unit,
              density,
              totalLiters,
              packageCount,
              densityRequired,
              layers,
              method,
              palletCount,
              groundPositions,
              baseArea,
              extraArea,
              totalArea
            };
          };

          const facilityLength =
            Math.max(
              0,
              Number(
                data.facility?.plotLengthM || 0
              )
            );

          const facilityWidth =
            Math.max(
              0,
              Number(
                data.facility?.plotWidthM || 0
              )
            );

          const north =
            Math.max(
              0,
              Number(
                data.facility?.setbackNorthM || 0
              )
            );

          const south =
            Math.max(
              0,
              Number(
                data.facility?.setbackSouthM || 0
              )
            );

          const east =
            Math.max(
              0,
              Number(
                data.facility?.setbackEastM || 0
              )
            );

          const west =
            Math.max(
              0,
              Number(
                data.facility?.setbackWestM || 0
              )
            );

          const facilityNetLength =
            facilityLength - north - south;

          const facilityNetWidth =
            facilityWidth - east - west;

          const facilityNetArea =
            facilityNetLength > 0 &&
            facilityNetWidth > 0
              ? facilityNetLength *
                facilityNetWidth
              : 0;

          const allowedStorageArea =
            facilityNetArea * 0.30;

          const usedStorageArea =
            data.chemicals.reduce(
              (sum, chem) =>
                sum +
                getChemicalStorage(chem).totalArea,
              0
            );

          const remainingStorageArea =
            allowedStorageArea -
            usedStorageArea;

          const storageUsagePercent =
            allowedStorageArea > 0
              ? (
                  usedStorageArea /
                  allowedStorageArea
                ) * 100
              : 0;

          const fmtStorage = (n) =>
            Number(n || 0).toLocaleString(
              'ar-SA',
              {
                maximumFractionDigits: 2
              }
            );

          const numberField = (
            label,
            value,
            onChange,
            placeholder = ''
          ) => (
            <div>
              <Label className="text-[10px] font-bold text-slate-600 mb-1 block">
                {label}
              </Label>

              <Input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={value ?? ''}
                placeholder={placeholder}
                onChange={(e) =>
                  onChange(
                    normalizeStorageNumber(
                      e.target.value
                    )
                  )
                }
                className="h-8 text-xs"
                dir="ltr"
              />
            </div>
          );

          return (
            <>
              {data.chemicals.map((chem) => {
                const calc =
                  getChemicalStorage(chem);

                return (
                  <div
                    key={chem.id}
                    className="border border-slate-200 rounded p-2 bg-slate-50/50 mb-2"
                  >

                    <div className="font-bold text-[#1a4d80] mb-1.5 text-sm">
                      {chem.name}
                    </div>

                    <div className="space-y-1.5">

                      <div>
                        <Label className="text-xs text-slate-700 mb-1 block flex items-center gap-1">
                          <Sparkles
                            size={10}
                            className="text-emerald-500"
                          />
                          {t('sds.panel.chemNameAuto')}
                        </Label>

                        <ChemicalAutocomplete
                          value={chem.name}
                          onChange={(v) =>
                            updChem(
                              chem.id,
                              'name',
                              v
                            )
                          }
                          onSelect={(picked) =>
                            applyChemicalTemplate(
                              chem.id,
                              picked
                            )
                          }
                          placeholder={
                            t(
                              'sds.panel.chemNamePh'
                            )
                          }
                        />
                      </div>

                      <TextInput
                        label={
                          t(
                            'sds.panel.chemChemName'
                          )
                        }
                        value={
                          chem.chemicalName
                        }
                        onChange={(v) =>
                          updChem(
                            chem.id,
                            'chemicalName',
                            v
                          )
                        }
                      />

                      <TextInput
                        label={
                          t('sds.panel.chemUn')
                        }
                        value={chem.unNumber}
                        onChange={(v) =>
                          updChem(
                            chem.id,
                            'unNumber',
                            v
                          )
                        }
                      />

                      <TextInput
                        label={
                          t('sds.panel.chemGhs')
                        }
                        value={
                          chem.ghsClassification
                        }
                        onChange={(v) =>
                          updChem(
                            chem.id,
                            'chemGhs',
                            v
                          )
                        }
                      />

                      <TextInput
                        label={
                          t('sds.panel.chemImage')
                        }
                        value={chem.image}
                        onChange={(v) =>
                          updChem(
                            chem.id,
                            'image',
                            v
                          )
                        }
                      />

                      <TextInput
                        label={
                          t('sds.panel.chemUsage')
                        }
                        value={chem.usage}
                        onChange={(v) =>
                          updChem(
                            chem.id,
                            'usage',
                            v
                          )
                        }
                      />

                      {/* Quantity + storage calculator */}
                      <div
                        dir="rtl"
                        className="mt-3 rounded-xl border border-[#b8c8d9] bg-white p-3"
                      >

                        <div className="mb-3 flex items-center justify-between gap-2">
                          <div>
                            <div className="text-xs font-bold text-[#1a4d80]">
                              الكمية ومساحة التخزين
                            </div>

                            <div className="mt-0.5 text-[9px] text-slate-500">
                              تحسب المساحة الأرضية المستخدمة لهذه المادة
                            </div>
                          </div>

                          {calc.totalArea > 0 && (
                            <div className="rounded-lg bg-blue-50 border border-blue-200 px-2 py-1 text-center">
                              <div className="text-[8px] text-blue-600">
                                المساحة
                              </div>

                              <div className="text-xs font-extrabold text-[#1a4d80]">
                                {fmtStorage(
                                  calc.totalArea
                                )} م²
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-2">

                          {numberField(
                            'كمية المادة',
                            chem.storageQuantity,
                            (v) =>
                              updChem(
                                chem.id,
                                'storageQuantity',
                                v
                              ),
                            'مثال: 4000'
                          )}

                          <div>
                            <Label className="text-[10px] font-bold text-slate-600 mb-1 block">
                              وحدة الكمية
                            </Label>

                            <select
                              value={
                                chem.storageQuantityUnit ||
                                'liter'
                              }
                              onChange={(e) =>
                                updChem(
                                  chem.id,
                                  'storageQuantityUnit',
                                  e.target.value
                                )
                              }
                              className="w-full h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                            >
                              <option value="liter">
                                لتر
                              </option>

                              <option value="kg">
                                كجم
                              </option>

                              <option value="ton">
                                طن
                              </option>

                              <option value="count">
                                عدد عبوات
                              </option>
                            </select>
                          </div>

                        </div>

                        {(calc.unit === 'kg' ||
                          calc.unit === 'ton') && (
                          <div className="mt-2">
                            {numberField(
                              'كثافة المادة (كجم/لتر)',
                              chem.storageDensityKgL,
                              (v) =>
                                updChem(
                                  chem.id,
                                  'storageDensityKgL',
                                  v
                                ),
                              'مثال: 1.25'
                            )}

                            {calc.densityRequired && (
                              <div className="mt-1 text-[9px] font-bold text-amber-700">
                                أدخل الكثافة من SDS لتحويل الوزن إلى حجم.
                              </div>
                            )}
                          </div>
                        )}

                        <div className="mt-3">

                          <Label className="text-[10px] font-bold text-slate-600 mb-1 block">
                            نوع العبوة
                          </Label>

                          <select
                            value={
                              calc.packageType
                            }
                            onChange={(e) =>
                              updChem(
                                chem.id,
                                'storagePackageType',
                                e.target.value
                              )
                            }
                            className="w-full h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                          >
                            {Object.entries(
                              packagePresets
                            ).map(
                              ([key, val]) => (
                                <option
                                  key={key}
                                  value={key}
                                >
                                  {val.label}
                                </option>
                              )
                            )}
                          </select>

                        </div>

                        {calc.packageType ===
                          'custom' && (
                          <div className="mt-2 grid grid-cols-3 gap-2">

                            {numberField(
                              'السعة (L)',
                              chem.storagePackageCapacityL,
                              (v) =>
                                updChem(
                                  chem.id,
                                  'storagePackageCapacityL',
                                  v
                                ),
                              '200'
                            )}

                            {numberField(
                              'الطول (م)',
                              chem.storagePackageLengthM,
                              (v) =>
                                updChem(
                                  chem.id,
                                  'storagePackageLengthM',
                                  v
                                ),
                              '0.60'
                            )}

                            {numberField(
                              'العرض (م)',
                              chem.storagePackageWidthM,
                              (v) =>
                                updChem(
                                  chem.id,
                                  'storagePackageWidthM',
                                  v
                                ),
                              '0.60'
                            )}

                          </div>
                        )}

                        <div className="mt-3 grid grid-cols-2 gap-2">

                          <div>
                            <Label className="text-[10px] font-bold text-slate-600 mb-1 block">
                              طريقة التخزين
                            </Label>

                            <select
                              value={
                                calc.method
                              }
                              onChange={(e) =>
                                updChem(
                                  chem.id,
                                  'storageMethod',
                                  e.target.value
                                )
                              }
                              className="w-full h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                            >
                              <option value="direct">
                                تخزين مباشر
                              </option>

                              <option value="pallet">
                                على طبالي
                              </option>
                            </select>
                          </div>

                          <div>
                            <Label className="text-[10px] font-bold text-slate-600 mb-1 block">
                              عدد الطبقات
                            </Label>

                            <select
                              value={
                                String(
                                  calc.layers
                                )
                              }
                              onChange={(e) =>
                                updChem(
                                  chem.id,
                                  'storageLayers',
                                  e.target.value
                                )
                              }
                              className="w-full h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                            >
                              <option value="1">
                                طبقة واحدة
                              </option>

                              <option value="2">
                                طبقتان
                              </option>

                              <option value="3">
                                ثلاث طبقات
                              </option>
                            </select>
                          </div>

                        </div>

                        {calc.method ===
                          'pallet' && (
                          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2">

                            <div className="grid grid-cols-2 gap-2">

                              <div>
                                <Label className="text-[10px] font-bold text-slate-600 mb-1 block">
                                  مقاس الطبلية
                                </Label>

                                <select
                                  value={
                                    chem.storagePalletType ||
                                    'iso'
                                  }
                                  onChange={(e) =>
                                    updChem(
                                      chem.id,
                                      'storagePalletType',
                                      e.target.value
                                    )
                                  }
                                  className="w-full h-8 rounded-md border border-slate-300 bg-white px-2 text-xs"
                                >
                                  {Object.entries(
                                    palletPresets
                                  ).map(
                                    ([key, val]) => (
                                      <option
                                        key={key}
                                        value={key}
                                      >
                                        {val.label}
                                      </option>
                                    )
                                  )}
                                </select>
                              </div>

                              {numberField(
                                'عدد العبوات/طبلية',
                                chem.storagePackagesPerPallet,
                                (v) =>
                                  updChem(
                                    chem.id,
                                    'storagePackagesPerPallet',
                                    v
                                  ),
                                'مثال: 4'
                              )}

                            </div>

                            {(chem.storagePalletType ===
                              'custom') && (
                              <div className="mt-2 grid grid-cols-2 gap-2">

                                {numberField(
                                  'طول الطبلية (م)',
                                  chem.storagePalletLengthM,
                                  (v) =>
                                    updChem(
                                      chem.id,
                                      'storagePalletLengthM',
                                      v
                                    ),
                                  '1.20'
                                )}

                                {numberField(
                                  'عرض الطبلية (م)',
                                  chem.storagePalletWidthM,
                                  (v) =>
                                    updChem(
                                      chem.id,
                                      'storagePalletWidthM',
                                      v
                                    ),
                                  '1.00'
                                )}

                              </div>
                            )}

                          </div>
                        )}

                        <div className="mt-2">
                          {numberField(
                            'مساحة إضافية للممرات/الاحتواء (م²)',
                            chem.storageExtraAreaM2,
                            (v) =>
                              updChem(
                                chem.id,
                                'storageExtraAreaM2',
                                v
                              ),
                            '0'
                          )}
                        </div>

                        <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-2">

                          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">

                            <div className="text-slate-500">
                              عدد العبوات
                            </div>

                            <div className="font-bold text-slate-800">
                              {fmtStorage(
                                calc.packageCount
                              )}
                            </div>

                            {calc.method ===
                              'pallet' && (
                              <>
                                <div className="text-slate-500">
                                  عدد الطبالي
                                </div>

                                <div className="font-bold text-slate-800">
                                  {fmtStorage(
                                    calc.palletCount
                                  )}
                                </div>
                              </>
                            )}

                            <div className="text-slate-500">
                              مواقع التخزين الأرضية
                            </div>

                            <div className="font-bold text-slate-800">
                              {fmtStorage(
                                calc.groundPositions
                              )}
                            </div>

                            <div className="text-slate-500">
                              مساحة البصمة
                            </div>

                            <div className="font-bold text-[#1a4d80]">
                              {fmtStorage(
                                calc.baseArea
                              )} م²
                            </div>

                            <div className="text-slate-500">
                              إجمالي مساحة المادة
                            </div>

                            <div className="font-extrabold text-[#1a4d80]">
                              {fmtStorage(
                                calc.totalArea
                              )} م²
                            </div>

                          </div>

                        </div>

                      </div>

                      <div>
                        <Label className="text-xs text-slate-700 mb-1 block">
                          {t(
                            'sds.panel.ghsSymbols'
                          )}
                        </Label>

                        <div className="grid grid-cols-2 gap-1">
                          {GHS_SYMBOL_OPTIONS.map(
                            (opt) => (
                              <Button
                                key={opt.id}
                                type="button"
                                variant={
                                  chem.ghsSymbols.includes(
                                    opt.id
                                  )
                                    ? 'default'
                                    : 'outline'
                                }
                                size="sm"
                                onClick={() =>
                                  toggleSymbol(
                                    chem.id,
                                    opt.id
                                  )
                                }
                                className="text-[11px] h-8 justify-center"
                              >
                                {i18n.language ===
                                'en'
                                  ? (
                                      opt.labelEn ||
                                      opt.label
                                    )
                                  : opt.label}
                              </Button>
                            )
                          )}
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs text-slate-700 mb-1 block">
                          {t(
                            'sds.panel.nfpaClassification'
                          )}
                        </Label>

                        <div className="grid grid-cols-4 gap-1">
                          {[
                            'health',
                            'fire',
                            'reactivity',
                            'special'
                          ].map((f) => (
                            <div key={f}>
                              <span className="text-[10px] text-slate-600">
                                {nfpaLabel(f)}
                              </span>

                              <Input
                                value={
                                  chem.nfpa[f]
                                }
                                onChange={(e) =>
                                  updNFPA(
                                    chem.id,
                                    f,
                                    e.target.value
                                  )
                                }
                                className="text-sm h-8 text-center"
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}

              {/* Combined facility storage summary */}
              <div
                dir="rtl"
                className="mt-4 rounded-xl border-2 border-[#1a4d80]/20 bg-gradient-to-br from-white to-blue-50 p-3"
              >

                <div className="text-sm font-bold text-[#1a4d80] mb-3">
                  ملخص مساحة تخزين جميع المواد
                </div>

                <div className="grid grid-cols-2 gap-2">

                  <div className="rounded-lg border border-slate-200 bg-white p-2 text-center">
                    <div className="text-[9px] text-slate-500">
                      الحد المسموح
                    </div>

                    <div className="font-extrabold text-[#1a4d80]">
                      {fmtStorage(
                        allowedStorageArea
                      )} م²
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-white p-2 text-center">
                    <div className="text-[9px] text-slate-500">
                      المستخدم
                    </div>

                    <div className="font-extrabold text-slate-800">
                      {fmtStorage(
                        usedStorageArea
                      )} م²
                    </div>
                  </div>

                  <div
                    className={`rounded-lg border p-2 text-center ${
                      remainingStorageArea >= 0
                        ? 'border-emerald-200 bg-emerald-50'
                        : 'border-red-300 bg-red-50'
                    }`}
                  >
                    <div className="text-[9px] text-slate-500">
                      المتبقي
                    </div>

                    <div
                      className={`font-extrabold ${
                        remainingStorageArea >= 0
                          ? 'text-emerald-700'
                          : 'text-red-700'
                      }`}
                    >
                      {fmtStorage(
                        remainingStorageArea
                      )} م²
                    </div>
                  </div>

                  <div
                    className={`rounded-lg border p-2 text-center ${
                      storageUsagePercent <= 100
                        ? 'border-emerald-200 bg-emerald-50'
                        : 'border-red-300 bg-red-50'
                    }`}
                  >
                    <div className="text-[9px] text-slate-500">
                      استخدام الحد
                    </div>

                    <div
                      className={`font-extrabold ${
                        storageUsagePercent <= 100
                          ? 'text-emerald-700'
                          : 'text-red-700'
                      }`}
                    >
                      {fmtStorage(
                        storageUsagePercent
                      )}%
                    </div>
                  </div>

                </div>

                {allowedStorageArea > 0 &&
                  usedStorageArea >
                    allowedStorageArea && (
                    <div className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[11px] font-bold text-red-700 text-center">
                      تجاوز مساحة التخزين الحد المسموح بنسبة 30%
                    </div>
                  )}

                {allowedStorageArea > 0 &&
                  usedStorageArea <=
                    allowedStorageArea && (
                    <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-[11px] font-bold text-emerald-700 text-center">
                      مساحة التخزين ضمن الحد المسموح
                    </div>
                  )}

              </div>
            </>
          );
        })()}
      </Section>

      <Section title={t('sds.panel.secHandling')}>
        <TextInput label={t('sds.panel.lblTitle')} value={data.safeHandling.title} onChange={(v) => update('safeHandling.title', v)} />
        <ListEditor
          label={t('sds.panel.lblItems')}
          items={data.safeHandling.items}
          onChange={(v) => update('safeHandling.items', v)}
          addLabel={addLabel}
        />
      </Section>

      <Section title={t('sds.panel.secPpe')}>
        <TextInput label={t('sds.panel.lblTitle')} value={data.ppe.title} onChange={(v) => update('ppe.title', v)} />
        <ListEditor label={t('sds.panel.lblItems')} items={data.ppe.items} onChange={(v) => update('ppe.items', v)} addLabel={addLabel} />
      </Section>

      <Section title={t('sds.panel.secStorage')}>
        <TextInput label={t('sds.panel.lblTitle')} value={data.safeStorage.title} onChange={(v) => update('safeStorage.title', v)} />
        <ListEditor label={t('sds.panel.lblItems')} items={data.safeStorage.items} onChange={(v) => update('safeStorage.items', v)} addLabel={addLabel} />
      </Section>

      <Section title={t('sds.panel.secSpill')}>
        <TextInput label={t('sds.panel.lblTitle')} value={data.spillProcedures.title} onChange={(v) => update('spillProcedures.title', v)} />
        <ListEditor label={t('sds.panel.lblItems')} items={data.spillProcedures.items} onChange={(v) => update('spillProcedures.items', v)} addLabel={addLabel} />
      </Section>

      <Section title={t('sds.panel.secEmergencyPlan')}>
        <TextInput label={t('sds.panel.lblTitle')} value={data.emergencyPlan.title} onChange={(v) => update('emergencyPlan.title', v)} />
        <ListEditor label={t('sds.panel.lblItems')} items={data.emergencyPlan.items} onChange={(v) => update('emergencyPlan.items', v)} addLabel={addLabel} />
      </Section>

      <Section title={t('sds.panel.secTraining')}>
        <TextInput label={t('sds.panel.lblTitle')} value={data.training.title} onChange={(v) => update('training.title', v)} />
        <ListEditor label={t('sds.panel.lblItems')} items={data.training.items} onChange={(v) => update('training.items', v)} addLabel={addLabel} />
      </Section>

      <Section title={t('sds.panel.secSafetyCulture')}>
        <TextInput label={t('sds.panel.lblTitle')} value={data.safetyCulture.title} onChange={(v) => update('safetyCulture.title', v)} />
        <TextArea label={t('sds.panel.lblText')} value={data.safetyCulture.text} onChange={(v) => update('safetyCulture.text', v)} />
        <TextInput label={t('sds.panel.lblMotto')} value={data.safetyCulture.motto} onChange={(v) => update('safetyCulture.motto', v)} />
      </Section>

      <Section title={t('sds.panel.secFooter')}>
        <TextInput label={t('sds.panel.lblPreparedBy')} value={data.footer.preparedBy} onChange={(v) => update('footer.preparedBy', v)} />
        <div>
          <Label className="text-xs text-slate-700 mb-1 block">{t('sds.panel.lblDate')}</Label>
          <div className="flex gap-1">
            <Input
              value={data.footer.date || ''}
              onChange={(e) => update('footer.date', e.target.value)}
              className="text-sm h-9 flex-1"
              placeholder="DD/MM/YYYY"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                const d = new Date();
                const dd = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const yyyy = d.getFullYear();
                update('footer.date', `${dd}/${mm}/${yyyy}`);
              }}
              className="text-xs h-9 px-3 whitespace-nowrap"
            >
              {t('sds.panel.lblToday')}
            </Button>
          </div>
        </div>
        <TextInput label={t('sds.panel.lblIssueNo')} value={data.footer.issueNumber} onChange={(v) => update('footer.issueNumber', v)} />
        <TextInput label={t('sds.panel.lblReviewNo')} value={data.footer.reviewNumber} onChange={(v) => update('footer.reviewNumber', v)} />
        <TextInput label={t('sds.panel.lblNextReview')} value={data.footer.nextReview} onChange={(v) => update('footer.nextReview', v)} />
        <TextArea label={t('sds.panel.lblNotice')} value={data.footer.notice} onChange={(v) => update('footer.notice', v)} />
      </Section>
    </div>
  );
}
